/**
 * Report artifact viewer — the chat split pane (mode "pane") and the full page /reports/:id (mode "page").
 * Toolbar: title pill + "≡" outline, ‹ › other reports, share (signed link via POST /api/artifacts/:id/share,
 * or the internal link), download (PDF via print, Markdown, JSON via /api/artifacts/:id/download), expand, ✕.
 */
import { ChevronLeft, ChevronRight, Download, Expand, FileJson, FileText, Link2, List, MessageSquare, Printer, Share2, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { artifactDownloadUrl, shareArtifact, type Artifact } from "@/api/chat";
import { useArtifact, useArtifactSiblings } from "@/api/chat-hooks";
import { errorMessage } from "@/api/client";
import { toast } from "@/api/toast-store";
import { cn } from "@/lib/cn";
import { ErrorState } from "../../ui/Feedback";
import { MenuItem, MenuLabel, PopoverPanel, Tooltip, usePopover } from "../../ui/Overlay";
import { copyText } from "../util";
import { ReportBody, sectionTitle } from "./ReportBody";

export function ArtifactViewer({
  artifactId,
  mode,
  brandId,
  onClose,
  onNavigate,
  onExpand,
}: {
  artifactId: string;
  mode: "pane" | "page";
  brandId?: string | null;
  onClose?: () => void;
  onNavigate: (id: string) => void;
  onExpand?: () => void;
}) {
  const q = useArtifact(artifactId, brandId);
  const a = q.data ?? null;
  const sib = useArtifactSiblings(a);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [artifactId]);

  // ← → navigate between reports when the viewer has focus and no field is focused.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (!scrollRef.current?.closest("[data-artifact-viewer]")?.contains(document.activeElement) && document.activeElement !== document.body) return;
      if (e.key === "ArrowLeft" && sib.prev) onNavigate(sib.prev.id);
      if (e.key === "ArrowRight" && sib.next) onNavigate(sib.next.id);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [sib.prev, sib.next, onNavigate]);

  const scrollToSection = (i: number) => {
    const el = scrollRef.current?.querySelector(`[data-section="${i}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-artifact-viewer className={cn("flex h-full min-h-0 flex-col", mode === "pane" ? "bg-[#161618]" : "bg-canvas")}>
      <div className={cn("sticky top-0 z-10 flex shrink-0 items-center gap-2 border-b border-line/60 bg-[#1b1b1d]/95 px-3 py-2.5 backdrop-blur sm:px-4", mode === "page" && "bg-canvas/90")}>
        <OutlinePill title={a?.title ?? (q.isLoading ? "Loading…" : "Report")} artifact={a} onSection={scrollToSection} />
        <div className="ml-auto flex shrink-0 items-center gap-0.5">
          <ToolbarButton label="Previous report" disabled={!sib.prev} onClick={() => sib.prev && onNavigate(sib.prev.id)}>
            <ChevronLeft className="size-4.5" />
          </ToolbarButton>
          <ToolbarButton label="Next report" disabled={!sib.next} onClick={() => sib.next && onNavigate(sib.next.id)}>
            <ChevronRight className="size-4.5" />
          </ToolbarButton>
          <span className="mx-1 h-5 w-px bg-line" aria-hidden />
          <ShareMenu artifactId={artifactId} disabled={!a} />
          <DownloadMenu artifact={a} onPrint={() => setPrinting(true)} />
          {mode === "pane" && onExpand && (
            <ToolbarButton label="Expand" accent onClick={onExpand}>
              <Expand className="size-4" />
            </ToolbarButton>
          )}
          {mode === "page" && a?.threadId && (
            <Tooltip content="Open the chat">
              <Link to={`/chat/${a.threadId}?report=${a.id}`} aria-label="Open the chat" className="grid size-8 place-items-center rounded-lg text-lavender hover:bg-hover">
                <MessageSquare className="size-4" />
              </Link>
            </Tooltip>
          )}
          {onClose && (
            <ToolbarButton label="Close" onClick={onClose}>
              <X className="size-4.5" />
            </ToolbarButton>
          )}
        </div>
      </div>
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto scrollbar-thin" tabIndex={-1}>
        <div className={cn("mx-auto w-full", mode === "page" ? "max-w-[920px] px-4 pb-20 pt-6 sm:px-8" : "max-w-[860px] px-4 pb-16 pt-4 sm:px-6")}>
          {q.isLoading ? (
            <div className="grid min-h-[50vh] place-items-center text-[14px] text-fg/80" role="status">
              Loading…
            </div>
          ) : q.error && !a ? (
            <ErrorState error={q.error} onRetry={() => void q.refetch()} />
          ) : a ? (
            <ReportBody artifact={a} compact={mode === "pane"} onRevision={onNavigate} />
          ) : null}
        </div>
      </div>
      {printing && a && <PrintReport artifact={a} onDone={() => setPrinting(false)} />}
    </div>
  );
}

function ToolbarButton({ label, children, onClick, disabled, accent }: { label: string; children: ReactNode; onClick?: () => void; disabled?: boolean; accent?: boolean }) {
  return (
    <Tooltip content={label} side="bottom">
      <button type="button" aria-label={label} onClick={onClick} disabled={disabled} className={cn("grid size-8 place-items-center rounded-lg transition-colors hover:bg-hover disabled:opacity-30 disabled:hover:bg-transparent", accent ? "text-lavender" : "text-fg/80 hover:text-fg")}>
        {children}
      </button>
    </Tooltip>
  );
}

function OutlinePill({ title, artifact, onSection }: { title: string; artifact: Artifact | null; onSection: (i: number) => void }) {
  const p = usePopover();
  return (
    <>
      <button
        type="button"
        ref={(el) => {
          p.anchorRef.current = el;
        }}
        onClick={p.toggle}
        disabled={!artifact}
        aria-label="Report outline"
        aria-expanded={p.open}
        className="flex h-9 min-w-0 max-w-[min(340px,60vw)] items-center gap-2 rounded-xl bg-elevated/70 px-3 text-[13.5px] text-fg/90 transition-colors hover:bg-elevated-2"
      >
        <span className="truncate">{title}</span>
        <List className="size-4 shrink-0 text-muted" />
      </button>
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} className="max-h-[60vh] w-72 overflow-y-auto p-1.5 scrollbar-thin">
        <MenuLabel>Outline</MenuLabel>
        {artifact?.limitations.length ? (
          <MenuItem
            onClick={() => {
              p.close();
              onSection(-1);
            }}
          >
            Limitations
          </MenuItem>
        ) : null}
        {artifact?.sections.map((s, i) => (
          <MenuItem
            key={i}
            onClick={() => {
              p.close();
              onSection(i);
            }}
          >
            {sectionTitle(s, i)}
          </MenuItem>
        ))}
      </PopoverPanel>
    </>
  );
}

function ShareMenu({ artifactId, disabled }: { artifactId: string; disabled?: boolean }) {
  const p = usePopover();
  const [busy, setBusy] = useState(false);
  const copy = async (url: string, title: string) => {
    const ok = await copyText(url);
    toast(ok ? { tone: "success", title } : { tone: "warning", title: "Couldn't copy", body: url });
  };
  return (
    <>
      <Tooltip content="Share" side="bottom">
        <button
          type="button"
          ref={(el) => {
            p.anchorRef.current = el;
          }}
          onClick={p.toggle}
          disabled={disabled}
          aria-label="Share"
          aria-expanded={p.open}
          className="grid size-8 place-items-center rounded-lg text-lavender transition-colors hover:bg-hover disabled:opacity-30"
        >
          <Share2 className="size-4" />
        </button>
      </Tooltip>
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} align="end" className="w-72 p-1.5">
        <MenuItem
          icon={<Link2 className="size-4" />}
          description="Anyone with the link can view a read-only copy for 7 days"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const s = await shareArtifact(artifactId);
              await copy(s.url, "Share link copied");
              p.close();
            } catch (e) {
              toast({ tone: "danger", title: "Couldn't create a share link", body: errorMessage(e) });
            } finally {
              setBusy(false);
            }
          }}
        >
          Copy share link
        </MenuItem>
        <MenuItem
          icon={<Link2 className="size-4" />}
          description="For teammates with access to this brand"
          onClick={() => {
            p.close();
            void copy(`${window.location.origin}/reports/${artifactId}`, "Link copied");
          }}
        >
          Copy internal link
        </MenuItem>
      </PopoverPanel>
    </>
  );
}

function DownloadMenu({ artifact, onPrint }: { artifact: Artifact | null; onPrint: () => void }) {
  const p = usePopover();
  const dl = (href: string) => {
    p.close();
    const a = document.createElement("a");
    a.href = href;
    a.rel = "noopener";
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  return (
    <>
      <Tooltip content="Download" side="bottom">
        <button
          type="button"
          ref={(el) => {
            p.anchorRef.current = el;
          }}
          onClick={p.toggle}
          disabled={!artifact}
          aria-label="Download"
          aria-expanded={p.open}
          className="grid size-8 place-items-center rounded-lg text-lavender transition-colors hover:bg-hover disabled:opacity-30"
        >
          <Download className="size-4" />
        </button>
      </Tooltip>
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} align="end" className="w-60 p-1.5">
        <MenuItem
          icon={<Printer className="size-4" />}
          description="Print or save as PDF"
          onClick={() => {
            p.close();
            onPrint();
          }}
        >
          PDF
        </MenuItem>
        <MenuItem icon={<FileText className="size-4" />} onClick={() => artifact && dl(artifactDownloadUrl(artifact.id, "md"))}>
          Markdown
        </MenuItem>
        <MenuItem icon={<FileJson className="size-4" />} onClick={() => artifact && dl(artifactDownloadUrl(artifact.id, "json"))}>
          JSON
        </MenuItem>
      </PopoverPanel>
    </>
  );
}

const PRINT_CSS = `
@media screen { .mimic-print-root { position: fixed; left: -100000px; top: 0; width: 820px; } }
@media print {
  @page { margin: 14mm; }
  body > *:not(.mimic-print-root) { display: none !important; }
  html, body { background: #fff !important; height: auto !important; overflow: visible !important; }
  .mimic-print-root { position: static; width: auto; }
  .mimic-print-root section, .mimic-print-root .rounded-2xl { break-inside: avoid; }
}
.mimic-print-root {
  --canvas: #fff; --surface: #fff; --surface-2: #f6f6f7; --surface-3: #f1f1f2; --elevated: #efeff1; --elevated-2: #e6e6e8;
  --hover: #f2f2f3; --border: #e2e2e5; --border-strong: #d0d0d4; --text: #111113; --text-muted: #55555c; --text-subtle: #77777e;
  --lavender: #4a38cc; --link: #4a38cc; background: #fff; color: #111113; padding: 8px 4px;
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
}
.mimic-print-root .bg-\\[\\#141416\\] { background: #fff !important; }
`;

/** Renders a light-themed copy of the report into <body>, prints it, then removes it. */
function PrintReport({ artifact, onDone }: { artifact: Artifact; onDone: () => void }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = artifact.title;
    const after = () => {
      document.title = prevTitle;
      onDone();
    };
    window.addEventListener("afterprint", after, { once: true });
    // Let charts measure + render before opening the dialog.
    const t = window.setTimeout(() => window.print(), 450);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("afterprint", after);
      document.title = prevTitle;
    };
  }, [artifact.title, onDone]);
  return createPortal(
    <div className="mimic-print-root" aria-hidden>
      <style>{PRINT_CSS}</style>
      <ReportBody artifact={artifact} animate={false} />
    </div>,
    document.body,
  );
}
